"use client";

import { faChevronDown, faSlidersH } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "@tosize/ui";
import { NativeInput, RoundedButton, SideDialog, useModal } from "@tosize/ui";
import { filter } from "fuzzy";
import { Accordion } from "radix-ui";
import { type ChangeEvent, Fragment, useDeferredValue, useMemo, useState } from "react";
import { FilterItem } from "~/app/[lang]/(default)/overview/[type]/[id]/DIYListing/FilterItem";
import { useDIYFilter } from "~/app/[lang]/(default)/overview/[type]/[id]/DIYListing/useDIYListing";
import type { DIYAggregation } from "~/graphql/shared/page";

interface FilterDialogProps {
	dialogTitle: string;
	aggregations: DIYAggregation[];
	closeMessage: string;
	searchMessage: string;
	emptyMessage: string;
}

export function FilterDialog(props: FilterDialogProps) {
	const { dialogTitle, aggregations, closeMessage, ...rest } = props;
	const dialog = useModal();

	return (
		<Fragment>
			<RoundedButton className="flex" onClick={dialog.toggle}>
				<span>{dialogTitle}</span>

				<FontAwesomeIcon icon={faSlidersH} className="ml-auto h-4 w-4" />
			</RoundedButton>

			<SideDialog
				title={dialogTitle}
				{...dialog.props}
				footer={
					<Button className="w-full" onClick={() => dialog.close()}>
						{closeMessage}
					</Button>
				}
			>
				<Accordion.Root type="multiple" className="divide-y divide-shades">
					{aggregations.map((aggregation) => (
						<FilterDialogItem key={aggregation.value} aggregation={aggregation} {...rest} />
					))}
				</Accordion.Root>
			</SideDialog>
		</Fragment>
	);
}

interface FilterDialogItemProps extends Pick<FilterDialogProps, "searchMessage" | "emptyMessage"> {
	aggregation: DIYAggregation;
}

function FilterDialogItem({ aggregation, searchMessage, emptyMessage }: FilterDialogItemProps) {
	const { activeOptions } = useDIYFilter({ aggregation });

	const [search, setSearch] = useState("");
	const query = useDeferredValue(search);

	function onSearchChange(evt: ChangeEvent<HTMLInputElement>) {
		setSearch(evt.target.value);
	}

	const options = useMemo(() => {
		const filtered = filter(query, aggregation.options, {
			pre: "<b>",
			post: "</b>",
			extract: (option) => option.label ?? "",
		});

		return filtered.map((result) => ({
			...(aggregation.options[result.index] as DIYAggregation["options"][number]),
			label: result.string,
		}));
	}, [query, aggregation.options]);

	return (
		<Accordion.Item value={aggregation.value} disabled={aggregation.options.length === 0}>
			<Accordion.Header>
				<Accordion.Trigger className="group flex w-full items-center py-4 disabled:text-shades-medium">
					<div className="mr-2 text-left font-bold group-hover:underline group-disabled:no-underline">
						{aggregation.label}
					</div>

					{activeOptions.length > 0 && (
						<span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-dark text-white text-xs">
							{activeOptions.length}
						</span>
					)}

					<FontAwesomeIcon
						icon={faChevronDown}
						className="ml-auto group-rdx-state-open:rotate-180"
					/>
				</Accordion.Trigger>
			</Accordion.Header>

			<Accordion.Content className="mb-4 space-y-4">
				{aggregation.options.length > 5 && (
					<NativeInput
						placeholder={`${searchMessage} ${aggregation.label.toLowerCase()}...`}
						aria-label={`${searchMessage} ${aggregation.label.toLowerCase()}...`}
						type="text"
						value={search}
						onChange={onSearchChange}
					/>
				)}

				{options.length === 0 ? (
					<div>{emptyMessage}</div>
				) : (
					options.map((option) => (
						<FilterItem key={option.value} aggregation={aggregation} option={option} />
					))
				)}
			</Accordion.Content>
		</Accordion.Item>
	);
}
